import { LayerOrientation } from "@buildwithflux/models";
import { Vector2, Vector3 } from "three";
import { removePolyHoles } from "../../../../../common";
import { createShapeFromDxfString } from "../../../../../shapes/dxf";
import { isDxfFileName, isSvgFileName, scaleFromUnit } from "../../../../../shapes/helpers";
import { createShapesFromPath, createSvgFromSvgPath, getPathsFromSvgString } from "../../../../../shapes/svg";
import { getRotatedPosition, toMm } from "./utils";
/**
 * Decodes a base64 string to a regular string.
 * Works in both browser and Node.js environments.
 */
function decodeBase64(base64) {
    if (typeof atob === "function") {
        return atob(base64);
    }
    // Node.js environment
    return Buffer.from(base64, "base64").toString("utf-8");
}
function scalePoint(point, br, yPositionMultiplier = 1, yPointMultiplier = 1, useUnitScale = false, invertedRotation = true) {
    const { scale } = br;
    const unitScale = useUnitScale ? scaleFromUnit(br.unit) : 1;
    const position = getRotatedPosition(new Vector3(point.x, point.y, 0), br.rotationRelativeToDocument, invertedRotation);
    return {
        x: toMm(br.positionRelativeToDocument.x + unitScale * scale.x * position.x),
        y: toMm(yPositionMultiplier * br.positionRelativeToDocument.y + yPointMultiplier * unitScale * scale.y * position.y),
    };
}
function getShapePoints(shape, br) {
    return shape.getPoints(64).flatMap((point) => scalePoint(point, br, -1, 1, true));
}
export function extractShapes(br) {
    if (br.asset?.payloadFileName && br.asset?.payloadAsBase64) {
        if (isSvgFileName(br.asset.payloadFileName)) {
            return extractSvgShapes(br);
        }
        else if (isDxfFileName(br.asset.payloadFileName)) {
            return extractDxfShapes(br);
        }
    }
    else if (br.linePath) {
        return extractSvgShapes(br);
    }
    return { polyShapes: [], lineShapes: [] };
}
function extractSvgShapes(br) {
    let svgString;
    if (br?.asset?.payloadAsBase64) {
        svgString = decodeBase64(br?.asset?.payloadAsBase64 ?? "");
    }
    else if (br.linePath) {
        svgString = createSvgFromSvgPath(br.linePath);
    }
    const polyShapes = [];
    const lineShapes = [];
    const paths = getPathsFromSvgString(svgString ?? "");
    const filledPaths = paths.filter((path) => path.userData?.style?.fill !== "none");
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    filledPaths.forEach((path) => {
        const shapes = createShapesFromPath(path);
        const hasStrokeColor = path.userData?.style.stroke !== "none";
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        shapes.forEach((shape) => {
            const points = getShapePoints(shape, br);
            const interiorPoints = shape
                .getPointsHoles(64)
                .map((hole) => hole.flatMap((point) => scalePoint(point, br, -1, 1, true)));
            let polyPoints = points.map((point) => ({ x: point.x, y: point.y }));
            if (interiorPoints.length > 0) {
                polyPoints = removePolyHoles(polyPoints, interiorPoints.map((pts) => pts.map((point) => new Vector2(point.x, point.y))));
            }
            const fpPoly = {
                pts: polyPoints,
                layer: br.layer === LayerOrientation.top ? "F.SilkS" : "B.SilkS",
                // Only send through stroke if it is visible on the SVG
                width: hasStrokeColor ? toMm(path.userData?.style.strokeWidth * br.scale.x) : 0,
            };
            polyShapes.push(fpPoly);
        });
    });
    const strokePaths = paths.filter((path) => path.userData?.style?.fill === "none");
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    strokePaths.forEach((path) => {
        const shapes = createShapesFromPath(path);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        shapes.forEach((shape) => {
            const points = getShapePoints(shape, br);
            const scaledPoints = points.map((point) => {
                return { x: point.x, y: point.y };
            });
            for (let i = 0; i < scaledPoints.length; i++) {
                const start = scaledPoints[i];
                const end = scaledPoints[i + 1];
                if (start && end) {
                    const position = {
                        start,
                        end,
                    };
                    const fpLine = {
                        ...position,
                        layer: br.layer === LayerOrientation.top ? "F.SilkS" : "B.SilkS",
                        width: toMm(path.userData?.style.strokeWidth * br.scale.x),
                    };
                    lineShapes.push(fpLine);
                }
            }
        });
    });
    return { polyShapes, lineShapes };
}
export function extractDxfShapes(br) {
    if (!br.asset?.payloadAsBase64) {
        return { polyShapes: [], lineShapes: [] };
    }
    const shape = createShapeFromDxfString(decodeBase64(br.asset.payloadAsBase64));
    if (!shape) {
        return { polyShapes: [], lineShapes: [] };
    }
    const polyShapes = [];
    const lineShapes = [];
    const points = shape.getPoints(64).flatMap((point) => scalePoint(point, br, -1, -1, false, false));
    const polyPoints = points.map((point) => new Vector2(point.x, point.y));
    const fpPoly = {
        pts: polyPoints,
        layer: br.layer === LayerOrientation.top ? "F.SilkS" : "B.SilkS",
    };
    polyShapes.push(fpPoly);
    return { polyShapes, lineShapes };
}
//# sourceMappingURL=customSilkShapes.js.map